/**
 * Softphone keyboard shortcuts.
 *
 * All shortcuts use the Alt+Shift+<letter> chord (Option+Shift on macOS —
 * same physical chord, `altKey + shiftKey` in code). Matching is done
 * against `KeyboardEvent.code` (the physical key), not `event.key`, because
 * macOS Option can remap the character `event.key` produces.
 */
import type {
  SoftphoneShortcutAction,
  SoftphoneShortcutDefinition,
} from 'Modules/softphone/types';
import getOS from 'Utils/getOS';

export const SOFTPHONE_SHORTCUTS: SoftphoneShortcutDefinition[] = [
  { action: 'mute', code: 'KeyM', letter: 'M' },
  { action: 'hold', code: 'KeyH', letter: 'H' },
  { action: 'accept', code: 'KeyA', letter: 'A' },
  { action: 'decline', code: 'KeyD', letter: 'D' },
  { action: 'end', code: 'KeyE', letter: 'E' },
  { action: 'keypad', code: 'KeyK', letter: 'K' },
  { action: 'video', code: 'KeyV', letter: 'V' },
  { action: 'transfer', code: 'KeyT', letter: 'T' },
  { action: 'merge', code: 'KeyG', letter: 'G' },
];

/**
 * OS-formatted key chips for an action's shortcut, e.g. `['Alt', 'Shift', 'M']`
 * on Windows/Linux or `['⌥', '⇧', 'M']` on macOS. Returns `[]` for an action
 * with no shortcut defined (e.g. not yet rolled out).
 */
export const getShortcutKeys = (action: SoftphoneShortcutAction): string[] => {
  const shortcut = SOFTPHONE_SHORTCUTS.find((s) => s.action === action);
  if (!shortcut) {
    return [];
  }

  const modifierKeys = getOS() === 'Mac' ? ['⌥', '⇧'] : ['Alt', 'Shift'];
  return [...modifierKeys, shortcut.letter];
};

/**
 * The chord in words, not glyphs — screen readers mispronounce `⌥`/`⇧` or skip
 * them. macOS says "Option", which is what is printed on the key.
 * {@link getShortcutKeys} draws the keycaps; this one is read aloud.
 */
export const getShortcutSpokenKeys = (
  action: SoftphoneShortcutAction
): string[] => {
  const shortcut = SOFTPHONE_SHORTCUTS.find((s) => s.action === action);
  if (!shortcut) {
    return [];
  }

  const modifierKeys = getOS() === 'Mac' ? ['Option', 'Shift'] : ['Alt', 'Shift'];
  return [...modifierKeys, shortcut.letter];
};

/**
 * Accessible name combining an action's label with its shortcut, e.g.
 * `"Mute, shortcut Alt+Shift+M"` — so assistive tech announces the shortcut
 * without requiring the visual tooltip to be open. Falls back to the plain
 * `label` when the action has no shortcut defined.
 */
export const getShortcutAriaLabel = (
  label: string,
  action: SoftphoneShortcutAction
): string => {
  const keys = getShortcutSpokenKeys(action);
  return keys.length > 0 ? `${label}, shortcut ${keys.join('+')}` : label;
};
